import { useState } from 'react'

import {
  EmployeeLegalEntitiesFilterDocument,
  EmployeeTeamsFilterDocument,
  GetWorkAreasDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { buildFiltersDefinition } from 'modules/shift_management/lib/multiFilters/buildFiltersDefinition'
import { buildOptions } from 'modules/shift_management/lib/multiFilters/buildOptions'
import { useSelectableLocations } from 'modules/shift_management/lib/useSelectableLocations'

interface UseMultiFilterSelectorProps {
  locationIds: number[]
}
const useMultiFilterSelector = ({ locationIds }: UseMultiFilterSelectorProps) => {
  const [teams, setTeams] = useState<Array<{ label: string; value: number }>>([])
  const [employeeStatuses, setEmployeeStatuses] = useState<Array<{ label: string; value: string }>>(
    []
  )
  const [status, setStatus] = useState<Array<{ label: string; value: string }>>([])
  const [legalEntities, setLegalEntities] = useState<Array<{ label: string; value: number }>>([])
  const [workAreas, setWorkAreas] = useState<Array<{ label: string; value: number }>>([])
  const [locations, setLocations] = useState<Array<{ label: string; value: number }>>([])

  const [{ view, shiftsConflicts, statsLoading }] = useSchedule()
  const { employee } = useCurrentSession()
  const [{ canSeeShiftsForEverybody }] = usePermissions()
  const { locations: selectableLocations } = useSelectableLocations(
    employee?.id,
    canSeeShiftsForEverybody
  )
  const { data: workAreasData } = useQuery(GetWorkAreasDocument, {
    variables: {
      locationIds,
    },
  })
  const { data: teamsData } = useQuery(EmployeeTeamsFilterDocument)
  const { data: legalEntitiesData } = useQuery(EmployeeLegalEntitiesFilterDocument)

  const { selectableEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt,
    'read',
    locationIds
  )

  const filterKeys = useUnsafeMemo(
    () => ({
      teams: i18n.t('filters.multi_filters.team'),
      employees: i18n.t('filters.multi_filters.employee'),
      status: i18n.t('filters.multi_filters.shift_status'),
      employee_status: i18n.t('filters.multi_filters.employee_status'),
      legal_entities: i18n.t('filters.multi_filters.legal_entity'),
      shift_work_areas: i18n.t('filters.multi_filters.shift_work_area'),
      shift_locations: i18n.t('filters.multi_filters.shift_location'),
    }),
    []
  )

  useUnsafeEffect(() => {
    const options = buildOptions({
      workAreasData,
      teamsData,
      legalEntitiesData,
      locationsData: selectableLocations,
    })
    setTeams(options.teamsOptions)
    setEmployeeStatuses(options.employeeStatusesOptions)
    setStatus(options.statusOptions)
    setLegalEntities(options.legalEntitiesOptions)
    setWorkAreas(options.workAreasOptions)
    setLocations(options.locationsOptions)
  }, [workAreasData, teamsData, legalEntitiesData, selectableLocations])

  const filtersDefinition = useUnsafeMemo(
    () =>
      buildFiltersDefinition({
        selectableEmployees,
        teams,
        employeeStatuses,
        status,
        legalEntities,
        workAreas,
        locations,
        filterKeys,
      }),
    [
      selectableEmployees,
      teams,
      employeeStatuses,
      status,
      legalEntities,
      workAreas,
      locations,
      filterKeys,
    ]
  )

  return {
    filtersDefinition,
    filterKeys,
    shiftsConflicts,
    view,
    statsLoading,
  }
}
export { useMultiFilterSelector }
